import { AxiosResponse } from 'axios'
import { environments } from 'environments/environments'
import { ReportManagementQueryDTO, ReportQueryDTO } from 'models/ReportQueryDTO'

import { DetranPrBillingReportQueryDTO } from 'models/ReportQueryDTO'
import {
    ClosureFilterDTO,
    ClouseResponseDTO
} from 'pages/FinancialManagement/components/Closure/types'
import {
    Query,
    ResponseDataFinancialReport
} from 'pages/FinancialManagement/components/Finance/types'
import { DashboardResponseDTO } from 'pages/Report/components/ManagementReport/ModeDashboard/interfaces'
import { ManagementReportInterface } from 'pages/Report/interface'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import api from './api'

const { ENDPOINT_REPORT_CSV, ENDPOINT_CONTRACTS, ENDPOINT_API_DETRANS } = environments

export const getCSVOperationalVisionReports = async (
    query: ReportQueryDTO,
    isBackoffice?: boolean,
    IF?: string,
    newLayoutCSV?: boolean,
): Promise<AxiosResponse<BlobPart>> => {
    if (newLayoutCSV) {
        if (isBackoffice) {
            return api.get(
                `${ENDPOINT_REPORT_CSV}/relatorios/instituicoes-financeiras/faturamentos?${new URLSearchParams(
                    Object.assign(
                        query
                            ? { ...query, instituicaoFinanceira: IF }
                            : { instituicaoFinanceira: IF }
                    )
                )}`
            )
        } else {
            return api.get(
                `${ENDPOINT_REPORT_CSV}/relatorios/instituicoes-financeiras/faturamentos?${new URLSearchParams(
                    Object.assign(query ? { ...query } : {})
                )}`
            )
        }
    } else {
        if (isBackoffice) {
            return api.get(
                `${ENDPOINT_REPORT_CSV}/relatorios/instituicoes-financeiras/operacoes/arquivos?${new URLSearchParams(
                    Object.assign(
                        query
                            ? { ...query, instituicaoFinanceira: IF }
                            : { instituicaoFinanceira: IF }
                    )
                )}`
            )
        } else {
            return api.get(
                `${ENDPOINT_REPORT_CSV}/relatorios/instituicoes-financeiras/operacoes/arquivos?${new URLSearchParams(
                    Object.assign(query ? { ...query } : {})
                )}`
            )
        }
    }
}

// RELATÓRIOS DE GESTÃO
interface ReportManagementQuery {
    pagina: number
    limite: number
    query: ReportManagementQueryDTO | null
}

export const getManagementReports = async (
    { pagina, limite, query }: ReportManagementQuery,
    IF?: string,
    isBackoffice?: boolean
): Promise<AxiosResponse<ManagementReportInterface>> => {
    const baseParams = { pagina: String(pagina), limite: String(limite) }
    const params = {
        ...baseParams,
        ...(query && query),
        ...(isBackoffice && { instituicaoFinanceira: IF ?? '' })
    }

    return api.get(
        `${ENDPOINT_CONTRACTS}/contratos/operacoes/eventos?${new URLSearchParams(
            params
        )}`
    )
}

export const getDashboardsManagementReports = async (
    query: ReportManagementQueryDTO | null,
    IF?: string
): Promise<AxiosResponse<DashboardResponseDTO>> => {
    const params = {
        ...(query && query),
        ...{ instituicaoFinanceira: IF ?? '' }
    }

    return api.get(
        `${ENDPOINT_REPORT_CSV}/graficos/operacoes/eventos?${new URLSearchParams(
            params
        )}`
    )
}

interface QueryCsv extends ReportManagementQueryDTO {
    tipo: string
    cnpjAgente: string
}

export const getFilesManagementReports = async (
    query: QueryCsv,
    IF?: string,
    isBackoffice?: boolean
): Promise<AxiosResponse<BlobPart>> => {
    if (isBackoffice) {
        return api.get(
            `${ENDPOINT_CONTRACTS}/contratos/operacoes/relatorios?${new URLSearchParams(
                Object.assign(
                    query
                        ? { ...query, instituicaoFinanceira: IF }
                        : { instituicaoFinanceira: IF }
                )
            )}`
        )
    } else {
        return api.get(
            `${ENDPOINT_CONTRACTS}/contratos/operacoes/relatorios?${new URLSearchParams(
                Object.assign(query ? { ...query } : {})
            )}`
        )
    }
}

export const getPendingImagesReport = async (
    IF?: string,
    AF?: string,
    isBackoffice?: boolean
): Promise<AxiosResponse<Blob>> => {
    const params = {
        ...(AF && { cnpjAgente: AF }),
        ...(isBackoffice && IF && { instituicaoFinanceira: IF })
    }

    return api.get(
        `${ENDPOINT_CONTRACTS}/contratos/operacoes/relatorios/imagens-pendentes`,
        {
            params,
            responseType: 'blob',
            headers: {
                Accept: 'text/csv'
            }
        }
    )
}

export const generateDashboardManagementReportPDF = async (
    query: QueryCsv,
    base64: string,
    IF?: string,
    isBackoffice?: boolean
): Promise<AxiosResponse> => {
    const urlParams = new URLSearchParams(query ? { ...query } : {})

    if (isBackoffice && IF) {
        urlParams.append('instituicaoFinanceira', IF)
    }

    const payload = { graficosBase64: base64 }

    return api.post(
        `${ENDPOINT_REPORT_CSV}/graficos/operacoes/eventos/exportar?${urlParams}`,
        payload
    )
}

/* RELATÓRIOS FINANCEIROS */
export const getFilesFinanceReports = async (
    query: Query,
    IF?: string,
    AF?: string
): Promise<AxiosResponse<BlobPart>> => {
    const urlParams = new URLSearchParams(
        new URLSearchParams(
            Object.assign(query ? { ...query } : { agrupar: false })
        )
    )

    if (IF) {
        urlParams.append('instituicaoFinanceira', IF)
    }
    if (AF) {
        urlParams.append('cnpjAgente', AF)
    }

    return api.get(
        `${ENDPOINT_CONTRACTS}/contratos/operacoes/financeiro-relatorios?${urlParams}`
    )
}

export const generateDashboardFinanceReportPDF = async (
    query: QueryCsv,
    base64: string,
    IF?: string,
    AF?: string,
    isBackoffice?: boolean
): Promise<AxiosResponse> => {
    const urlParams = new URLSearchParams(query ? { ...query } : {})

    if (isBackoffice && IF) {
        urlParams.append('instituicaoFinanceira', IF)
    }
    urlParams.append('cnpjAgente', AF ?? '')

    const payload = { graficosBase64: base64 }

    return api.post(
        `${ENDPOINT_REPORT_CSV}/graficos/operacoes/financeiro/exportar?${urlParams}`,
        payload
    )
}

export const getFinanceReports = async (
    query: Query,
    IF?: string
): Promise<AxiosResponse<ResponseDataFinancialReport>> => {
    return api.get(
        `${ENDPOINT_CONTRACTS}/contratos/operacoes/financeiro-eventos?${new URLSearchParams(
            Object.assign(
                query
                    ? { ...query, instituicaoFinanceira: IF ?? '' }
                    : IF
                        ? { instituicaoFinanceira: IF ?? '' }
                        : {}
            )
        )}`
    )
}

// RELATORÍROS DE FECHAMENTO
interface ClosureParams extends Partial<ClosureFilterDTO> {
    pagina: number
    limite: number
    cnpjAgente: string
}

export const getClosureReports = async (
    query: ClosureParams,
    IF: string
): Promise<AxiosResponse<ClouseResponseDTO>> => {
    const queryParams: any = {
        ...(query ?? {}),
        ...(IF && { instituicaoFinanceira: IF })
    }

    removeEmptyValues(queryParams)

    const formatParams = new URLSearchParams({ ...queryParams })

    const url = `${ENDPOINT_CONTRACTS}/contratos/operacoes/financeiro-sintetico?${formatParams}`

    return api.get(url)
}

export const generateDashboardClosureReportPDF = async (
    query: Omit<ClosureParams, 'pagina' | 'limite'>,
    base64: string,
    IF?: string,
    AF?: string,
    isBackoffice?: boolean
): Promise<AxiosResponse> => {
    const removeEmptyValuesQuery = removeEmptyValues(query)
    const urlParams = new URLSearchParams({ ...removeEmptyValuesQuery })
    urlParams.append('instituicaoFinanceira', IF ?? '')
    urlParams.append('cnpjAgente', AF ?? '')

    const payload = { graficosBase64: base64 }

    return api.post(
        `${ENDPOINT_REPORT_CSV}/graficos/operacoes/financeiro-sintetico/exportar?${urlParams}`,
        payload
    )
}

export const getFilesClosureReports = async (
    query: Omit<ClosureParams, 'pagina' | 'limite'>,
    IF?: string
): Promise<AxiosResponse<BlobPart>> => {
    const removeEmptyValuesQuery = removeEmptyValues(query)
    const urlParams = new URLSearchParams({ ...removeEmptyValuesQuery })
    urlParams.append('instituicaoFinanceira', IF ?? '')

    return api.get(
        `${ENDPOINT_CONTRACTS}/contratos/operacoes/financeiro-sintetico-relatorios?${urlParams}`
    )
}

/* ARQUIVOS REMESSA */
export const getShippingFile = async (query: any) => {
    const removeEmptyValuesQuery = removeEmptyValues(query)
    const urlParams = new URLSearchParams({ ...removeEmptyValuesQuery })
    return api.get(`${ENDPOINT_REPORT_CSV}/relatorios/remessa?${urlParams}`)
}

/* DECLARAÇÃO DE INTEIRO TEOR E RESUMIDA */
export const getDeclarationReports = async (
    id: string,
    resumida?: boolean
): Promise<AxiosResponse<BlobPart>> => {
    const urlParams = new URLSearchParams({})
    if (resumida !== undefined) {
        urlParams.append('resumida', String(resumida))
    }

    return api.get(
        `${ENDPOINT_CONTRACTS}/contratos/${id}/declaracao?${urlParams}`
    )
}

const getDetranPrReportPdf = async (
    path: 'oficio' | 'cobranca' | 'boletos/mensal' | 'boletos/emitidos',
    query: DetranPrBillingReportQueryDTO
): Promise<AxiosResponse<Blob>> => {
    const queryParams = removeEmptyValues(query)
    const urlParams = new URLSearchParams({ ...(queryParams ?? {}) })

    return api.get(`${ENDPOINT_API_DETRANS}/detrans/pr/relatorios/${path}?${urlParams}`, {
        responseType: 'blob',
        headers: {
            Accept: '*/*',
        }
    })
}

export const getDetranPrCollectionLetter = async (
    query: DetranPrBillingReportQueryDTO
): Promise<AxiosResponse<Blob>> => {
    return getDetranPrReportPdf('oficio', query)
}

export const getDetranPrBillingReport = async (
    query: DetranPrBillingReportQueryDTO
): Promise<AxiosResponse<Blob>> => {
    return getDetranPrReportPdf('cobranca', query)
}

export const getDetranPrMonthlyBillStatusReport = async (
    query: DetranPrBillingReportQueryDTO
): Promise<AxiosResponse<Blob>> => {
    return getDetranPrReportPdf('boletos/mensal', query)
}

export const getDetranPrIssuedBillsReport = async (
    query: DetranPrBillingReportQueryDTO
): Promise<AxiosResponse<Blob>> => {
    return getDetranPrReportPdf('boletos/emitidos', query)
}

export const getDetranRnDetailedDebtReport = (
    identificadorDebito: number
): Promise<AxiosResponse<Blob>> => {
    return api.get(
        `${ENDPOINT_API_DETRANS}/detrans/rn/guias/${identificadorDebito}/detalhes`,
        {
            responseType: 'blob',
            headers: { Accept: 'application/pdf' }
        }
    )
}

export interface DetranManagementReportQueryDTO {
    ufDetran: string
    tipoRelatorio: string
    cnpjAgenteFinanceiro: string
    referencia: string
    pagina?: number
    limite?: number
}

export interface DetranManagementReportResponse {
    registros: Record<string, any>[]
    totalRegistros: number
    numeroPagina: number
    tamanhoPagina: number
    totalPaginas: number
    hasPreviousPage: boolean
    hasNextPage: boolean
}

export const getDetranManagementReports = async (
    query: DetranManagementReportQueryDTO
): Promise<AxiosResponse<DetranManagementReportResponse>> => {
    const params = removeEmptyValues({ ...(query ?? {}) })

    return api.get(`${ENDPOINT_REPORT_CSV}/detrans/relatorios/gestao`, {
        params
    })
}

export const getDetranManagementReportFile = async (
    query: DetranManagementReportQueryDTO & { tipo: string }
): Promise<AxiosResponse<Blob>> => {
    const params = removeEmptyValues({ ...(query ?? {}) })

    return api.get(
        `${ENDPOINT_REPORT_CSV}/detrans/relatorios/gestao/exportar`,
        {
            params,
            responseType: 'blob',
            headers: {
                Accept: '*/*'
            }
        }
    )
}
